import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import camelize from 'camelize';
import striptags from 'striptags';
import urlJoin from 'url-join';
import { urls } from 'app-constants';
import renderBlockErrors from './renderBlockErrors';
import Select from 'components/common/Select';
import QuillEditor from 'components/common/QuillEditor';
import MediaRelationField from 'components/views/CustomTitleBuilder/CustomTitleField/MediaRelationField';


const ImageWithText = ({
  uid,
  errors,
  media,
  description,
  descriptionSource,
  credit,
  creditSource,
  linkUrl,
  imageSize,
  imageAlignment,
  content,
  onPreviewTextUpdate,
  onChange,
}) => {
  const [attachedMedia, setAttachedMedia] = useState(null);

  useEffect(() => {
    onPreviewTextUpdate(striptags(content || ''));
  }, [content]);

  const handleFieldChange = (name, val) => onChange({ [name]: val });

  const sizeOptions = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
  ];
  const sizeSelectVal = sizeOptions.find(opt => opt.value === imageSize);

  const alignOptions = [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
  ];
  const alignSelectVal = alignOptions.find(opt => opt.value === imageAlignment);

  const fetchData = mediaId => {
    if (!mediaId) return;
    const url = urlJoin(urls.mediaDataBase, mediaId, '?format=json');
    fetch(url, { credentials: 'include' })
      .then(response => {
        if (!response.ok) {
          console.error(response.statusText);
        }
        return response;
      })
      .then(response => response.json())
      .then(data => camelize(data))
      .then(data => setAttachedMedia(data))
      .catch(err => console.error(err));
  };

  useEffect(() => {
    if (media) {
      fetchData(media);
    } else {
      setAttachedMedia(null);
    }
  }, [media]);

  const formatDesc = val => striptags(val.replace(/<\/p>\s*<p>/gi, '</p>\n\n<p>'));

  const descriptionSourceOptions = [
    { value: 'media_long_desc', label: 'Use media long description' },
    { value: 'media_short_desc', label: 'Use media short description' },
    { value: 'custom', label: 'Use custom description' },
    { value: 'none', label: 'None' },
  ];
  const descriptionSourceValue = descriptionSourceOptions.find(opt => opt.value === descriptionSource);

  const creditSourceOptions = [
    { value: 'media_credit', label: 'Use media credit' },
    { value: 'custom', label: 'Use custom credit' },
    { value: 'none', label: 'None' },
  ];
  const creditSourceValue = creditSourceOptions.find(opt => opt.value === creditSource);

  let derivedDescriptionValue = '';
  if (descriptionSource === 'media_long_desc') {
    derivedDescriptionValue = attachedMedia ? formatDesc(attachedMedia.longDescription) : '';
  }
  if (descriptionSource === 'media_short_desc') {
    derivedDescriptionValue = attachedMedia ? formatDesc(attachedMedia.shortDescription) : '';
  }
  const descriptionInput = descriptionSource === 'custom' ? (
    <QuillEditor
      value={description}
      onChange={val => handleFieldChange('description', val)}
      size="small"
    />
  ) : (
    <textarea
      key={`${uid}-description-${descriptionSource}`}
      cols="30"
      rows="3"
      className="form-input"
      value={derivedDescriptionValue}
      disabled
    />
  );

  let derivedCreditValue = '';
  if (creditSource === 'media_credit') {
    derivedCreditValue = attachedMedia ? striptags(attachedMedia.credit) : '';
  }
  const creditInput = creditSource === 'custom' ? (
    <input
      key={`${uid}-credit-${creditSource}`}
      type="text"
      className="form-input"
      value={credit}
      onChange={e => handleFieldChange('credit', e.target.value)}
    />
  ) : (
    <input
      key={`${uid}-credit-${creditSource}`}
      type="text"
      className="form-input"
      value={derivedCreditValue}
      disabled
    />
  );

  const renderedErrors = renderBlockErrors(errors);

  return (
    <div className="content">
      <MediaRelationField
        value={media}
        label="Image:"
        limitTypes={['image']}
        onChange={val => handleFieldChange('media', val)}
      />
      {renderedErrors.media}

      <div className="mb-3">
        <div>Image Description:</div>
        <div className="mb-2">
          <Select
            value={descriptionSourceValue}
            options={descriptionSourceOptions}
            onChange={val => handleFieldChange('descriptionSource', val)}
          />
          {renderedErrors.descriptionSource}
        </div>
        <div>
          {descriptionInput}
          {renderedErrors.description}
        </div>
      </div>

      <div className="mb-3">
        <div>Image Credit:</div>
        <div className="mb-1">
          <Select
            value={creditSourceValue}
            options={creditSourceOptions}
            onChange={val => handleFieldChange('creditSource', val)}
          />
          {renderedErrors.creditSource}
        </div>
        <div>
          {creditInput}
          {renderedErrors.credit}
        </div>
      </div>

      <div className="mb-3">
        <div>Image Link URL:</div>
        <div className="mb-1">
          <input
            type="text"
            className="form-input"
            value={linkUrl || ''}
            onChange={e => handleFieldChange('linkUrl', e.target.value)}
          />
          {renderedErrors.linkUrl}
          <div className="text-hint mt-1">
            Optionally enter a URL for the image to link to.
          </div>
        </div>
      </div>

      <div className="mb-3">
        <div>Image Size:</div>
        <div className="mb-1">
          <Select
            value={sizeSelectVal}
            options={sizeOptions}
            onChange={val => handleFieldChange('imageSize', val)}
          />
          {renderedErrors.imageSize}
        </div>
      </div>

      <div className="mb-3">
        <div>Image Alignment:</div>
        <div className="mb-1">
          <Select
            value={alignSelectVal}
            options={alignOptions}
            onChange={val => handleFieldChange('imageAlignment', val)}
          />
          {renderedErrors.imageAlignment}
        </div>
      </div>

      <div className="mb-3">
        <div>Text Content:</div>
        <div>
          <QuillEditor
            value={content}
            onChange={val => handleFieldChange('content', val)}
            size="small"
          />
          {renderedErrors.content}
        </div>
      </div>
    </div>
  );
};

ImageWithText.propTypes = {
  uid: PropTypes.string,
  errors: PropTypes.object,
  media: PropTypes.string,
  description: PropTypes.string,
  descriptionSource: PropTypes.oneOf(['media_long_desc', 'media_short_desc', 'custom', 'none']),
  credit: PropTypes.string,
  creditSource: PropTypes.oneOf(['media_credit', 'custom', 'none']),
  linkUrl: PropTypes.string,
  imageSize: PropTypes.oneOf(['small', 'medium', 'large']),
  imageAlignment: PropTypes.oneOf(['left', 'right']),
  content: PropTypes.string,
  onPreviewTextUpdate: PropTypes.func,
  onChange: PropTypes.func,
};

export default ImageWithText;
